/**
 * 目標・進捗の型と入力検証（02 5.3.10 goal）。
 * - 目標コードは自動採番だけ（ブリーフ C-03）。本文に goal_code があれば定義外の項目として 400（strict：D-019）
 * - 目標値・現在値・進捗の値は符号ありの Decimal 文字列（zSignedMoney）。応答も小数 2 桁の Decimal 文字列（02 2.11）
 * - 登録の「終了日は開始日以降」「個人の目標は担当者が必須（D-046）」はこのスキーマで確かめ、更新は保存済みの値と合わせて service で確かめる
 */
import { z } from 'zod';
import { GOAL_SCOPE_TYPE_VALUES } from '../enums/goal-scope-type';
import { GOAL_STATUS_VALUES } from '../enums/goal-status';
import { GOAL_TYPE_VALUES } from '../enums/goal-type';
import { METRIC_TYPE_VALUES } from '../enums/metric-type';
import { PERIOD_TYPE_VALUES } from '../enums/period-type';
import { compareDate, isValidJstDate } from '../date/jst';
import { zCode, zDate, zEnum, zRequiredText, zShortText, zSignedMoney, zSystemCode, zVersion } from './common.schema';
import { zNullable } from './customer.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 目標タグ（顧客のタグとは別物。各 50 文字・20 個まで：02 5.3.10） */
export const zGoalTags = z.array(zRequiredText(50)).max(20, 'MSG-V-020');

/** 期間の日付（未入力は MSG-V-048「開始日と終了日を入力してください」） */
const zGoalDate = z.string({ message: 'MSG-V-048' }).min(1, 'MSG-V-048').pipe(zDate);

export const GOAL_INPUT_SHAPE = {
  goal_name: zRequiredText(200, 'MSG-V-094'),
  goal_type: zEnum(GOAL_TYPE_VALUES),
  goal_scope_type: zEnum(GOAL_SCOPE_TYPE_VALUES),
  period_type: zEnum(PERIOD_TYPE_VALUES),
  goal_status: zEnum(GOAL_STATUS_VALUES).optional(),
  description: zShortText(2000).optional(),
  start_date: zGoalDate,
  end_date: zGoalDate,
  metric_type: zEnum(METRIC_TYPE_VALUES),
  metric_unit: zShortText(20).optional(),
  target_value: z.string({ message: 'MSG-V-093' }).min(1, 'MSG-V-093').pipe(zSignedMoney),
  current_value: zNullable(zSignedMoney).optional(),
  parent_goal_code: zNullable(zCode).optional(),
  assignee_user_code: zNullable(zSystemCode).optional(),
  goal_tags: zGoalTags.optional(),
};

export interface GoalRuleValues {
  goal_scope_type?: string | undefined;
  assignee_user_code?: string | null | undefined;
  start_date?: string | undefined;
  end_date?: string | undefined;
}

export interface GoalRuleIssue {
  field: 'end_date' | 'assignee_user_code';
  code: 'FE-MSG-V-006' | 'FE-MSG-V-015';
}

/** 項目をまたぐ決まり（終了日は開始日以降：FE-MSG-V-006、個人の目標は担当者が必須：FE-MSG-V-015・D-046） */
export function goalRuleIssues(values: GoalRuleValues): GoalRuleIssue[] {
  const issues: GoalRuleIssue[] = [];
  if (
    values.start_date &&
    values.end_date &&
    isValidJstDate(values.start_date) &&
    isValidJstDate(values.end_date) &&
    compareDate(values.end_date, values.start_date) < 0
  ) {
    issues.push({ field: 'end_date', code: 'FE-MSG-V-006' });
  }
  if (values.goal_scope_type === 'personal' && !values.assignee_user_code) {
    issues.push({ field: 'assignee_user_code', code: 'FE-MSG-V-015' });
  }
  return issues;
}

/** API-GOAL-02 目標の登録 */
export const GoalCreateSchema = z
  .object(GOAL_INPUT_SHAPE)
  .strict()
  .superRefine((values, context) => {
    for (const issue of goalRuleIssues(values)) {
      context.addIssue({ code: 'custom', path: [issue.field], message: issue.code });
    }
  });

/** API-GOAL-04 目標の更新（すべて任意＋version。項目をまたぐ決まりは保存済みの値と合わせて service で確かめる） */
export const GoalUpdateSchema = z
  .object({
    goal_name: GOAL_INPUT_SHAPE.goal_name.optional(),
    goal_type: GOAL_INPUT_SHAPE.goal_type.optional(),
    goal_scope_type: GOAL_INPUT_SHAPE.goal_scope_type.optional(),
    period_type: GOAL_INPUT_SHAPE.period_type.optional(),
    goal_status: GOAL_INPUT_SHAPE.goal_status,
    description: GOAL_INPUT_SHAPE.description,
    start_date: GOAL_INPUT_SHAPE.start_date.optional(),
    end_date: GOAL_INPUT_SHAPE.end_date.optional(),
    metric_type: GOAL_INPUT_SHAPE.metric_type.optional(),
    metric_unit: GOAL_INPUT_SHAPE.metric_unit,
    target_value: GOAL_INPUT_SHAPE.target_value.optional(),
    current_value: GOAL_INPUT_SHAPE.current_value,
    parent_goal_code: GOAL_INPUT_SHAPE.parent_goal_code,
    assignee_user_code: GOAL_INPUT_SHAPE.assignee_user_code,
    goal_tags: GOAL_INPUT_SHAPE.goal_tags,
    version: zVersion,
  })
  .strict();

/** API-GOAL-09 進捗の記録 */
export const GoalProgressCreateSchema = z
  .object({
    progress_value: z.string({ message: 'MSG-V-020' }).min(1, 'MSG-V-020').pipe(zSignedMoney),
    progress_memo: zShortText(1000).optional(),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const GoalSchema = z
  .object({
    goal_code: z.string(),
    goal_name: z.string(),
    goal_type: z.enum(GOAL_TYPE_VALUES),
    goal_scope_type: z.enum(GOAL_SCOPE_TYPE_VALUES),
    period_type: z.enum(PERIOD_TYPE_VALUES),
    goal_status: z.enum(GOAL_STATUS_VALUES),
    description: z.string().nullable(),
    start_date: z.string(),
    end_date: z.string(),
    metric_type: z.enum(METRIC_TYPE_VALUES),
    metric_unit: z.string().nullable(),
    target_value: z.string(),
    current_value: z.string().nullable(),
    /** custom 以外：monthly_summaries と GSI から出した実績（02 9.6）。custom は null */
    calculated_value: z.string().nullable(),
    /** 表示に使う実績（custom は current_value、それ以外は calculated_value） */
    progress_value: z.string(),
    remaining_value: z.string(),
    progress_rate: z.string(),
    last_progress_at: z.string().nullable(),
    parent_goal_code: z.string().nullable(),
    parent_goal_name: z.string().nullable(),
    /** 親目標が削除済み（02 2.9「<参照先>_deleted」・5.3.10 API-GOAL-05） */
    parent_goal_deleted: z.boolean(),
    assignee_user_code: z.string().nullable(),
    assignee_user_name: z.string().nullable(),
    goal_tags: z.array(z.string()),
    children_count: z.number().int(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const GoalProgressSchema = z.object({
  goal_code: z.string(),
  recorded_at: z.string(),
  progress_value: z.string(),
  progress_memo: z.string().nullable(),
  recorded_by: z.string(),
  recorded_by_name: z.string().nullable(),
});

export const GoalProgressResponseSchema = z.object({ progress: GoalProgressSchema, goal: GoalSchema });

export const GOAL_LIST_SORT_VALUES = ['goal_code', 'goal_name', 'end_date', 'created_at'] as const;

/** API-GOAL-01（02 2.3.2） */
export const GoalListQuerySchema = buildListQuerySchema(GOAL_LIST_SORT_VALUES, 'goal_code').extend({
  goal_type: zEnum(GOAL_TYPE_VALUES).optional(),
  goal_status: zEnum(GOAL_STATUS_VALUES).optional(),
  goal_scope_type: zEnum(GOAL_SCOPE_TYPE_VALUES).optional(),
  period_type: zEnum(PERIOD_TYPE_VALUES).optional(),
  assignee_user_code: zSystemCode.optional(),
  parent_goal_code: zCode.optional(),
});

export type GoalCreateInput = z.infer<typeof GoalCreateSchema>;
export type GoalUpdateInput = z.infer<typeof GoalUpdateSchema>;
export type GoalProgressCreateInput = z.infer<typeof GoalProgressCreateSchema>;
export type Goal = z.infer<typeof GoalSchema>;
export type GoalProgress = z.infer<typeof GoalProgressSchema>;
export type GoalProgressResponse = z.infer<typeof GoalProgressResponseSchema>;
export type GoalListQuery = z.infer<typeof GoalListQuerySchema>;
